import { useState, useEffect } from 'react';
import { Search, CreditCard, Download, Eye, PieChart, Clock, XCircle, Loader2 } from 'lucide-react';
import { getTransactions, getTransactionDetails } from '../services/stripeService';
import AdminNav from '../components/AdminNav';
import SEO from '../components/SEO';
import { m } from 'framer-motion';

interface Transaction {
  id: string;
  amount: number;
  amountRefunded: number;
  currency: string;
  status: string;
  date: string;
  customer: {
    email: string | null | undefined;
    name: string | null | undefined;
  } | null;
  paymentMethod: string | undefined;
  description: string | null | undefined;
  refunded: boolean;
  disputed: boolean;
  failureCode: string | null;
  failureMessage: string | null;
  receiptUrl: string | null;
  paymentIntent: string | null;
  refunds: Array<{
    id: string;
    amount: number;
    status: string;
    reason: string | null;
    date: string;
  }>;
  dispute: {
    status: string;
    reason: string;
    amount: number;
  } | null;
}

interface TransactionDetails extends Transaction {
  receipt: string | null | undefined;
  metadata: Record<string, any>;
}

// Fonction pour animer un compteur
const Counter = ({ value, currency = 'EUR' }: { value: number, currency?: string }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const duration = 1500; // 1.5 secondes
    const steps = 60; // 60 étapes
    const stepValue = value / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      setDisplayValue(_prev => {
        const newValue = currentStep * stepValue;
        return newValue > value ? value : newValue;
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setDisplayValue(value);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <span>
      {new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency
      }).format(displayValue)}
    </span>
  );
};

const AdminTransactions = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      setIsLoading(true);
      const data = await getTransactions();
      setTransactions(data);
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Erreur lors du chargement des transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (transactionId: string) => {
    try {
      const details = await getTransactionDetails(transactionId);
      setSelectedTransaction(details);
    } catch (error: any) {
      setError(error.message || 'Erreur lors du chargement des détails');
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'processing':
      case 'requires_payment_method':
      case 'requires_confirmation':
      case 'requires_action':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'canceled':
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'succeeded':
        return 'Réussie';
      case 'processing':
        return 'En cours';
      case 'requires_payment_method':
        return 'Paiement requis';
      case 'requires_confirmation':
        return 'Confirmation requise';
      case 'requires_action':
        return 'Action requise';
      case 'canceled':
        return 'Annulée';
      case 'failed':
        return 'Échouée';
      default:
        return status;
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    const searchString = searchQuery.toLowerCase();
    return (
      transaction.id.toLowerCase().includes(searchString) ||
      (transaction.customer?.email || '').toLowerCase().includes(searchString) ||
      (transaction.customer?.name || '').toLowerCase().includes(searchString) ||
      (transaction.description || '').toLowerCase().includes(searchString)
    );
  });

  // Animation variants pour les cartes de statistiques
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const iconVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.2, rotate: 360, transition: { duration: 0.5 } }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-dark-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-gray-600 dark:text-gray-400" />
          <div className="text-lg text-gray-600 dark:text-gray-400">Chargement des transactions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-dark-900 py-12">
      <SEO data={{
        title: "Administration - Transactions",
        description: "",
        keywords: [],
        openGraph: {
          title: "Administration - Transactions",
          description: "",
          type: "website"
        },
        twitterCard: "summary_large_image",
        robots: "noindex, nofollow"
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdminNav />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Transactions
          </h1>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <m.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={0}
            whileHover={{ scale: 1.02 }}
            className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total encaissé</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  <Counter 
                    value={transactions.reduce((acc, t) => acc + (t.status === 'succeeded' ? t.amount - t.amountRefunded : 0), 0)} 
                  />
                </p>
              </div>
              <m.div
                variants={iconVariants}
                initial="initial"
                whileHover="hover"
                className="bg-green-100 dark:bg-green-900/20 p-3 rounded-lg"
              >
                <CreditCard className="h-6 w-6 text-green-600 dark:text-green-400" />
              </m.div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <m.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-gray-500 dark:text-gray-400"
              >
                {transactions.filter(t => t.status === 'succeeded').length} transactions réussies
              </m.span>
            </div>
          </m.div>

          <m.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={1}
            whileHover={{ scale: 1.02 }}
            className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">En attente</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  <Counter 
                    value={transactions.reduce((acc, t) => acc + (
                      ['processing', 'requires_payment_method', 'requires_confirmation', 'requires_action'].includes(t.status) ? t.amount : 0
                    ), 0)}
                  />
                </p>
              </div>
              <m.div
                variants={iconVariants}
                initial="initial"
                whileHover="hover"
                className="bg-blue-100 dark:bg-blue-900/20 p-3 rounded-lg"
              >
                <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </m.div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <m.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="text-gray-500 dark:text-gray-400"
              >
                {transactions.filter(t => ['processing', 'requires_payment_method', 'requires_confirmation', 'requires_action'].includes(t.status)).length} transactions en cours
              </m.span>
            </div>
          </m.div>

          <m.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={2}
            whileHover={{ scale: 1.02 }}
            className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Échecs</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  <Counter 
                    value={transactions.reduce((acc, t) => acc + (
                      ['canceled', 'failed'].includes(t.status) ? t.amount : 0
                    ), 0)}
                  />
                </p>
              </div>
              <m.div
                variants={iconVariants}
                initial="initial"
                whileHover="hover"
                className="bg-red-100 dark:bg-red-900/20 p-3 rounded-lg"
              >
                <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
              </m.div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <m.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-gray-500 dark:text-gray-400"
              >
                {transactions.filter(t => ['canceled', 'failed'].includes(t.status)).length} transactions échouées
              </m.span>
            </div>
          </m.div>

          <m.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            custom={3}
            whileHover={{ scale: 1.02 }}
            className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Taux de conversion</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                  <Counter 
                    value={transactions.length > 0 ? 
                      (transactions.filter(t => t.status === 'succeeded').length / transactions.length) * 100 : 0
                    } 
                  />%
                </p>
              </div>
              <m.div
                variants={iconVariants}
                initial="initial"
                whileHover="hover"
                className="bg-purple-100 dark:bg-purple-900/20 p-3 rounded-lg"
              >
                <PieChart className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </m.div>
            </div>
            <div className="mt-4 flex items-center text-sm">
              <m.span 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="text-gray-500 dark:text-gray-400"
              >
                {transactions.length} transactions au total
              </m.span>
            </div>
          </m.div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une transaction..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-dark-700 rounded-lg focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white dark:bg-dark-800 dark:text-white"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-dark-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
              <thead className="bg-gray-50 dark:bg-dark-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-700">
                {filteredTransactions.map((transaction, index) => (
                  <tr key={transaction.id} className={index % 2 === 0
                    ? 'bg-white dark:bg-dark-800'
                    : 'bg-gray-50 dark:bg-dark-700/50'
                  }>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(transaction.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-mono">
                      {transaction.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {transaction.customer ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {transaction.customer.name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {transaction.customer.email}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          Client anonyme
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatAmount(transaction.amount, transaction.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                        {getStatusLabel(transaction.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(transaction.id)}
                        className="text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 mr-3"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal des détails de la transaction */}
      {selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-800 rounded-lg max-w-2xl w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Détails de la transaction
                </h3>
                <button
                  onClick={() => setSelectedTransaction(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ID</p>
                    <p className="font-mono text-gray-900 dark:text-white">{selectedTransaction.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Date</p>
                    <p className="text-gray-900 dark:text-white">{formatDate(selectedTransaction.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Montant</p>
                    <p className="text-gray-900 dark:text-white">
                      {formatAmount(selectedTransaction.amount, selectedTransaction.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Statut</p>
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedTransaction.status)}`}>
                      {getStatusLabel(selectedTransaction.status)}
                    </span>
                  </div>
                </div>

                {selectedTransaction.customer && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Client</p>
                    <p className="text-gray-900 dark:text-white">{selectedTransaction.customer.name}</p>
                    <p className="text-gray-600 dark:text-gray-300">{selectedTransaction.customer.email}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Méthode de paiement</p>
                  <p className="text-gray-900 dark:text-white capitalize">{selectedTransaction.paymentMethod}</p>
                </div>

                {selectedTransaction.description && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Description</p>
                    <p className="text-gray-900 dark:text-white">{selectedTransaction.description}</p>
                  </div>
                )}

                {selectedTransaction.refunds.length > 0 && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Remboursements</p>
                    <div className="space-y-2">
                      {selectedTransaction.refunds.map(refund => (
                        <div key={refund.id} className="bg-gray-50 dark:bg-dark-700 p-3 rounded-lg">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatAmount(refund.amount, selectedTransaction.currency)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {new Date(refund.date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          {refund.reason && (
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                              Raison : {refund.reason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedTransaction.dispute && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Litige</p>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {formatAmount(selectedTransaction.dispute.amount, selectedTransaction.currency)}
                        </span>
                        <span className={`px-2 text-xs font-medium rounded-full ${
                          selectedTransaction.dispute.status === 'won' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-200' :
                          selectedTransaction.dispute.status === 'lost' ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-200' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200'
                        }`}>
                          {selectedTransaction.dispute.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Raison : {selectedTransaction.dispute.reason}
                      </p>
                    </div>
                  </div>
                )}

                {selectedTransaction.failureMessage && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Message d'erreur</p>
                    <p className="text-red-600 dark:text-red-400 text-sm">
                      {selectedTransaction.failureMessage}
                      {selectedTransaction.failureCode && ` (${selectedTransaction.failureCode})`}
                    </p>
                  </div>
                )}

                {selectedTransaction.metadata && selectedTransaction.metadata.template_link && (
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Template</p>
                    <a
                      href={selectedTransaction.metadata.template_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 underline"
                    >
                      Voir le template
                    </a>
                  </div>
                )}

                {selectedTransaction.receiptUrl && (
                  <div>
                    <a
                      href={selectedTransaction.receiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger le reçu
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactions;